import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '../AssignMentorDialog';

export const GRADES = [9, 10, 11, 12];
export const PROGRAM_STATUS = [
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];
export const PROGRAM_PILL = {
  draft: 'bg-gray-100 text-gray-500', open: 'bg-emerald-50 text-emerald-600', closed: 'bg-red-50 text-red-500',
};
export const STAGE_PILL = {
  draft: 'bg-gray-100 text-gray-500',
  accepting_offers: 'bg-violet-50 text-violet-600',
  accepting_applications: 'bg-[#FFF0ED] text-[#F06651]',
  allocating: 'bg-amber-50 text-amber-700',
  running: 'bg-sky-50 text-sky-700',
  awaiting_completion: 'bg-blue-50 text-blue-600',
  completed: 'bg-emerald-50 text-emerald-600',
  closed: 'bg-gray-100 text-gray-500',
};
export const OPP_PILL = {
  submitted: 'bg-violet-50 text-violet-600', changes_requested: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-600', rejected: 'bg-red-50 text-red-500',
  awaiting_company: 'bg-[#FFF0ED] text-[#F06651]',
};
export const OPP_LABEL = {
  submitted: 'Awaiting review', changes_requested: 'Changes requested',
  approved: 'Approved', rejected: 'Rejected', awaiting_company: 'Awaiting company confirmation',
};
export const SOURCE_LABEL = {
  mentor: 'Mentor', invited: 'Invited', company: 'Company',
  partner: 'Partner', parent_referral: 'Parent referral',
};
export const INVITE_PILL = {
  sent: 'bg-blue-50 text-blue-600', opened: 'bg-amber-50 text-amber-700',
  submitted: 'bg-emerald-50 text-emerald-600', revoked: 'bg-gray-100 text-gray-500',
};
export const FORMAT_LABEL = { on_site: 'On-site', remote: 'Remote', hybrid: 'Hybrid' };
export const CAREER_PILL = {
  mapped: 'bg-emerald-50 text-emerald-600', pending: 'bg-amber-50 text-amber-700',
  rejected: 'bg-red-50 text-red-600', unmapped: 'bg-gray-100 text-gray-500',
};
export const CAREER_LABEL = {
  mapped: 'Career mapped', pending: 'Pending career review',
  rejected: 'Career not approved', unmapped: 'No career yet',
};
export const EMPTY_PROGRAM = {
  name: '', description: '', eligible_grades: [9, 10, 11, 12],
  start_date: '', end_date: '', application_deadline: '', status: 'draft',
};

export const ATTENTION_META = {
  offers_to_approve: { label: 'Offers awaiting your approval', tab: 'companies' },
  career_review: { label: 'Offers waiting on CareersPOV career review', tab: 'companies', info: true },
  referrals_to_review: { label: 'Parent proposals to review', tab: 'companies' },
  company_responses: { label: 'Companies yet to respond to proposals', tab: 'applications' },
  finalizations_pending: { label: 'Accepted students ready to finalise', tab: 'applications' },
  parent_confirmations: { label: 'Families yet to confirm a placement', tab: 'placements' },
  instructions_to_approve: { label: 'Joining instructions awaiting approval', tab: 'placements' },
  completions_to_verify: { label: 'Completions awaiting school verification', tab: 'placements' },
};

export const YOUR_ACTION_KINDS = [
  'offers_to_approve', 'referrals_to_review', 'finalizations_pending',
  'instructions_to_approve', 'completions_to_verify',
];

/** Attention metadata with a safe fallback, so a new backend kind never breaks the page. */
export const attentionMeta = (kind) => ATTENTION_META[kind]
  || { label: kind.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()), tab: 'overview' };

export const formatDayYear = (d) => (d ? `${formatDay(d)} ${d.slice(0, 4)}` : '—');

export const Spinner = () => <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>;
export const Empty = ({ text, testId }) => (
  <p className="text-center py-12 text-[#9CA3AF] text-sm max-w-md mx-auto leading-relaxed" data-testid={testId}>{text}</p>
);
export const Detail = ({ label, value }) => (value ? (
  <div><span className="text-[#9CA3AF] text-[11px]">{label}</span><p className="text-[#1F2937] whitespace-pre-wrap">{value}</p></div>
) : null);

/** Big number tile used on the internship overviews. */
export const Metric = ({ label, value, icon: Icon, tone = 'default', testId }) => (
  <div className="h-full flex flex-col justify-between bg-white rounded-2xl border border-[#EFF0F2] px-4 py-3.5" data-testid={testId}>
    <div className="flex items-start gap-2 min-h-[2.1rem]">
      {Icon && <Icon className={`h-3.5 w-3.5 mt-px shrink-0 ${tone === 'accent' ? 'text-[#F97D6A]' : 'text-[#9CA3AF]'}`} />}
      <p className="text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold leading-tight">{label}</p>
    </div>
    <p className="text-2xl font-bold text-[#1F2937] font-['Outfit'] mt-1 leading-none">{value}</p>
  </div>
);

export function ProgramDialog({ program, onClose, onSaved }) {
  const isNew = !program.id;
  const [form, setForm] = useState({
    name: program.name || '', description: program.description || '',
    eligible_grades: program.eligible_grades || [9, 10, 11, 12],
    start_date: program.start_date || '', end_date: program.end_date || '',
    application_deadline: program.application_deadline || '', status: program.status || 'draft',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleGrade = (g) => setForm(f => ({
    ...f,
    eligible_grades: f.eligible_grades.includes(g) ? f.eligible_grades.filter(x => x !== g) : [...f.eligible_grades, g].sort((a, b) => a - b),
  }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post('/coordinator/internship-programs', form);
      else await api.put(`/coordinator/internship-programs/${program.id}`, form);
      toast.success(isNew ? 'Programme created' : 'Programme updated');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save this programme'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="program-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">{isNew ? 'New Internship Program' : 'Edit Program'}</DialogTitle>
          <DialogDescription>Companies and mentors submit opportunities against this programme.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Program name *</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Summer Internships 2027" className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="program-name" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Description</Label>
            <Textarea value={form.description} onChange={e => set('description', e.target.value)} className="mt-1.5 rounded-xl border-gray-200 min-h-[70px] resize-none" data-testid="program-description" />
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Eligible grades *</Label>
            <div className="flex gap-2 mt-1.5">
              {GRADES.map(g => (
                <button key={g} type="button" onClick={() => toggleGrade(g)}
                  className={`h-11 flex-1 rounded-xl text-sm font-semibold border transition-colors ${form.eligible_grades.includes(g) ? 'bg-[#FFF0ED] border-[#F97D6A] text-[#F06651]' : 'bg-white border-gray-200 text-[#6B7280] hover:border-[#F97D6A]'}`}
                  data-testid={`program-grade-${g}`}>{g}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Internships start *</Label>
              <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="program-start" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Internships end *</Label>
              <Input type="date" min={form.start_date} value={form.end_date} onChange={e => set('end_date', e.target.value)} className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="program-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Application deadline *</Label>
              <Input type="date" max={form.start_date} value={form.application_deadline} onChange={e => set('application_deadline', e.target.value)} className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="program-deadline" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Status *</Label>
              <Select value={form.status} onValueChange={v => set('status', v)}>
                <SelectTrigger className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="program-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROGRAM_STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-[#9CA3AF] leading-relaxed">
            Only <strong>Open</strong> programmes accept submissions and appear to companies and families.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold" data-testid="save-program-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{isNew ? 'Create Program' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
